import React from 'react';
import { Container, Row, Col, Alert, Button } from 'react-bootstrap';
import { TabWarningIcon } from './SvgIcons';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { 
            hasError: false, 
            error: null, 
            errorInfo: null,
            errorId: null
        };
    }

    static getDerivedStateFromError(error) {
        // Don't show error UI for chunk loading errors - we'll reload the page instead
        if (error?.name === 'ChunkLoadError' || error?.message?.includes('Loading chunk')) {
            return { hasError: false };
        }
        
        // Update state so the next render will show the fallback UI
        return { 
            hasError: true,
            errorId: Date.now().toString(36) + Math.random().toString(36).substr(2)
        };
    }

    componentDidCatch(error, errorInfo) {
        // Handle chunk loading errors by reloading the page
        // This occurs when a new deployment happens and old chunks are no longer available
        if (error?.name === 'ChunkLoadError' || error?.message?.includes('Loading chunk')) {
            window.location.reload();
            return;
        }

        // Log error details for debugging
        console.error('ErrorBoundary caught an error:', error, errorInfo);
        
        this.setState({
            error: error,
            errorInfo: errorInfo
        });

        // Report error to monitoring service if available
        if (window.reportError) {
            window.reportError(error, errorInfo);
        }
    }

    handleRetry = () => {
        this.setState({ 
            hasError: false, 
            error: null, 
            errorInfo: null,
            errorId: null
        });
    }

    handleReload = () => {
        window.location.reload();
    }

    render() {
        if (this.state.hasError) {
            const { fallback: Fallback, showDetails = false } = this.props;
            
            // Use custom fallback if provided
            if (Fallback) {
                return <Fallback 
                    error={this.state.error} 
                    errorInfo={this.state.errorInfo}
                    onRetry={this.handleRetry}
                    onReload={this.handleReload}
                />;
            }

            // Default error UI
            return (
                <Container className="error-boundary-container mt-5">
                    <Row className="justify-content-center">
                        <Col md={8} lg={6}>
                            <Alert variant="danger" className="text-center">
                                <Alert.Heading>
                                    <TabWarningIcon size={32} style={{ marginBottom: '1rem' }} />
                                    <br />
                                    Something went wrong
                                </Alert.Heading>
                                <p>
                                    We're sorry, but something unexpected happened. 
                                    Please try refreshing the page or contact support if the problem persists.
                                </p>
                                
                                {showDetails && this.state.error && (
                                    <details className="mt-3 text-left">
                                        <summary style={{ cursor: 'pointer' }}>
                                            <small>Error Details (ID: {this.state.errorId})</small>
                                        </summary>
                                        <pre className="mt-2 p-2 bg-light border rounded" style={{ fontSize: '0.8rem', overflow: 'auto' }}>
                                            {this.state.error.toString()}
                                            {this.state.errorInfo.componentStack}
                                        </pre>
                                    </details>
                                )}
                                
                                <hr />
                                <div className="d-flex justify-content-center gap-2">
                                    <Button 
                                        variant="outline-danger" 
                                        onClick={this.handleRetry}
                                        className="me-2"
                                    >
                                        <i className="icon-refresh"></i> Try Again
                                    </Button>
                                    <Button 
                                        variant="danger" 
                                        onClick={this.handleReload}
                                    >
                                        <i className="icon-reload"></i> Reload Page
                                    </Button>
                                </div>
                            </Alert>
                        </Col>
                    </Row>
                </Container>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
